import { donorBulkPermission } from '../../../api/src/auth/permissions';
import { DonorImportJobs } from './DonorImportJobs';
import { EditorOnly } from '../components/EditorOnly';
import { useSearchParams } from 'react-router';
import { DonorBulkDialog, DONOR_ACTION_LABELS } from './DonorBulkDialog';
import {
  DONOR_CANDIDATE_RULES,
  DONOR_CANDIDATE_FRESH_HOURS,
  DONOR_CONFIRMATION_GAP_HOURS,
  PROBLEM_LABELS,
  type DonorBulkInput,
  type DonorFilters,
  type DonorActionView,
  type DonorBulkAction,
} from '../../../api/src/donors/donors.types';
import { DonorSettingsDialog } from './DonorSettingsDialog';
import { DonorImportDialog } from './DonorImportDialog';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
} from '@mui/material';
import { api } from '../api/client';
import type {
  DonorArticleView,
  DonorHistoryItem,
  DonorLookupItem,
  DonorList,
  DonorState,
} from '../../../api/src/donors/donors.types';
import { PageHeader } from '../components/PageHeader';
import { Stack, Typography } from '../components/mui-system';
import { useWorkspace } from '../workspace-context';

const labels: Record<string, string> = {
  unchecked: 'Не проверено',
  found: 'Ссылка найдена',
  changed: 'Ссылка изменилась',
  missing: 'В HTML не найдена',
  unavailable: 'Статья недоступна',
  unknown: 'Не удалось проверить',
};
const date = (value: string | null) => (value ? new Date(value).toLocaleString('ru-RU') : '—');
const message = (error: unknown) => (error instanceof Error ? error.message : 'Ошибка запроса');
const color = (status: string): 'success' | 'warning' | 'error' | 'default' =>
  status === 'found'
    ? 'success'
    : status === 'changed'
      ? 'warning'
      : ['missing', 'unavailable'].includes(status)
        ? 'error'
        : 'default';

export function DonorsPage() {
  const { activeWorkspace } = useWorkspace();
  return <DonorsWorkspacePage key={activeWorkspace!.id} workspace={activeWorkspace!.id} />;
}
function DonorsWorkspacePage({ workspace }: { workspace: string }) {
  const [params, setParams] = useSearchParams();
  const donorId = params.get('donor');
  const [linkAttempt, setLinkAttempt] = useState(0);
  const [linkError, setLinkError] = useState('');
  const [data, setData] = useState<DonorList | null>(null);
  const [query, setQuery] = useState<DonorFilters & { page: number }>({
    page: 1,
    search: '',
    target: '',
    status: '',
    scope: 'active',
  });
  const [checked, setChecked] = useState<string[]>([]);
  const [allMatching, setAllMatching] = useState(false);
  const [bulkInput, setBulkInput] = useState<DonorBulkInput | null>(null);
  const [actions, setActions] = useState<DonorActionView[]>([]);
  function changeFilters(next: typeof query) {
    setQuery(next);
    setChecked([]);
    setAllMatching(false);
    setData(null);
  }
  function openBulk(action: DonorBulkAction, ids?: string[]) {
    const filter = { ...query };
    delete (filter as Partial<typeof query>).page;
    setBulkInput({ action, filter, ...(ids ? { ids } : allMatching ? {} : { ids: checked }) });
  }
  const archived = query.scope === 'archived';
  const selectedCount = allMatching ? (data?.total ?? 0) : checked.length;
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<DonorArticleView | 'new' | null>(null);
  const [selected, setSelected] = useState<DonorArticleView | null>(null);
  const [history, setHistory] = useState<DonorHistoryItem[]>([]);
  const [importRevision, setImportRevision] = useState(0);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const generation = useRef(0);
  useEffect(() => {
    if (!donorId) return;
    let current = true;
    void (async () => {
      try {
        const row = await api.donor(workspace, donorId);
        const [checks, events] = await Promise.all([
          api.donorHistory(workspace, row.id),
          api.donorActions(workspace, row.id),
        ]);
        if (!current) return;
        setSelected(row);
        setHistory(checks);
        setActions(events);
        const next = new URLSearchParams(params);
        next.delete('donor');
        setParams(next, { replace: true });
      } catch (caught) {
        if (current) setLinkError(`Не удалось открыть статью из Реестра: ${message(caught)}`);
      }
    })();
    return () => {
      current = false;
    };
  }, [donorId, workspace, params, setParams, linkAttempt]);
  const refresh = useCallback(async () => {
    const version = ++generation.current;
    try {
      const result = await api.donors(workspace, query);
      if (version === generation.current) setData(result);
    } catch (caught) {
      if (version === generation.current) setError(message(caught));
    }
  }, [workspace, query]);
  useEffect(() => {
    const start = setTimeout(() => void refresh(), 250);
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 15_000);
    return () => {
      clearTimeout(start);
      clearInterval(timer);
      generation.current++;
    };
  }, [refresh]);
  async function action(run: () => Promise<unknown>, success?: string) {
    setBusy(true);
    setError('');
    try {
      await run();
      if (success) setNotice(success);
      await refresh();
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }
  async function openDetail(row: DonorArticleView) {
    setSelected(row);
    setHistory([]);
    setActions([]);
    try {
      const [checks, events] = await Promise.all([
        api.donorHistory(workspace, row.id),
        api.donorActions(workspace, row.id),
      ]);
      setHistory(checks);
      setActions(events);
    } catch (caught) {
      setError(message(caught));
    }
  }
  return (
    <>
      <PageHeader
        title="Доноры"
        description="Статьи со ссылками на ваши сайты. Частота проверки меняется от 1 до 7 дней."
        action={
          <>
            <Button variant="outlined" onClick={() => setSettingsOpen(true)}>
              Настройки
            </Button>
            <EditorOnly permissions="donors.import">
              <Button component="label" variant="outlined" disabled={busy}>
                Импорт CSV
                <input
                  hidden
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file && file.size > 4_000_000) setError('CSV превышает 4 МБ');
                    else if (file) setImportFile(file);
                    event.target.value = '';
                  }}
                />
              </Button>
            </EditorOnly>
            <EditorOnly permissions="donors.create">
              <Button variant="contained" onClick={() => setEditing('new')}>
                Добавить статью
              </Button>
            </EditorOnly>
          </>
        }
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {linkError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              onClick={() => {
                setLinkError('');
                setLinkAttempt((value) => value + 1);
              }}
            >
              Повторить
            </Button>
          }
        >
          {linkError}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>
          {notice}
        </Alert>
      )}
      <DonorImportJobs workspace={workspace} revision={importRevision} />
      {data && !data.worker.online && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Проверяющий процесс не запущен или потерял связь. Последний сигнал:{' '}
          {date(data.worker.lastSeenAt)}. Записи сохранены и ожидают проверки.{' '}
          <Link href="/docs/donors#запуск-worker-и-ограничения">Как запускается checker</Link>
        </Alert>
      )}
      {data?.worker.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {data.worker.error}
        </Alert>
      )}
      <Stack direction="row" flexWrap="wrap" gap={1} mb={2} alignItems="center">
        <Chip label={`Активных: ${data?.stats.total ?? '…'}`} />
        <Chip
          color="success"
          variant="outlined"
          label={`Найдены: ${data?.stats.statuses.found ?? 0}`}
        />
        <Chip variant="outlined" label={`Ожидают проверки: ${data?.stats.due ?? 0}`} />
        <EditorOnly permissions={data?.paused ? 'donors.run' : 'donors.stop'}>
          <Button
            disabled={busy || !data}
            onClick={() => void action(() => api.pauseDonors(workspace, !data!.paused))}
          >
            {data?.paused ? 'Продолжить мониторинг' : 'Пауза'}
          </Button>
        </EditorOnly>
        <EditorOnly permissions="donors.run">
          <Button disabled={busy || !data?.stats.total} onClick={() => setConfirmAll(true)}>
            Полная перепроверка всех
          </Button>
        </EditorOnly>
        <Button onClick={() => void refresh()}>Обновить</Button>
      </Stack>
      {data?.paused && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Мониторинг на паузе. Уже начатые проверки могут завершиться.
        </Alert>
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} gap={1} mb={2}>
        <TextField
          label="Поиск донора"
          helperText="Введите домен, URL статьи или заметку"
          slotProps={{ htmlInput: { maxLength: 200 } }}
          value={query.search}
          onChange={(event) => changeFilters({ ...query, page: 1, search: event.target.value })}
          size="small"
          fullWidth
        />
        <TextField
          label="Наш домен или целевой URL"
          value={query.target}
          onChange={(event) => changeFilters({ ...query, page: 1, target: event.target.value })}
          size="small"
          fullWidth
        />
        <TextField
          select
          label="Состояние"
          value={query.status}
          onChange={(event) => changeFilters({ ...query, page: 1, status: event.target.value })}
          size="small"
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">Все состояния</MenuItem>
          {Object.entries(labels).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={1} mb={2} alignItems="center">
        <TextField
          select
          size="small"
          label="Список"
          value={query.scope}
          sx={{ minWidth: 160 }}
          onChange={(event) =>
            changeFilters({
              ...query,
              page: 1,
              scope: event.target.value as 'active' | 'archived',
              candidates: undefined,
            })
          }
        >
          <MenuItem value="active">Активные</MenuItem>
          <MenuItem value="archived">Архив</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Причина"
          value={query.problem ?? ''}
          sx={{ minWidth: 230 }}
          onChange={(event) =>
            changeFilters({
              ...query,
              page: 1,
              problem: event.target.value
                ? (event.target.value as DonorFilters['problem'])
                : undefined,
            })
          }
        >
          <MenuItem value="">Все причины</MenuItem>
          {Object.entries(PROBLEM_LABELS).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="HTTP-код"
          type="number"
          value={query.httpStatus ?? ''}
          sx={{ width: 130 }}
          slotProps={{ htmlInput: { min: 100, max: 599 } }}
          onChange={(event) =>
            changeFilters({
              ...query,
              page: 1,
              httpStatus: event.target.value ? Number(event.target.value) : undefined,
            })
          }
        />
        <TextField
          select
          size="small"
          label="Проблема длится"
          value={query.problemDays ?? 0}
          sx={{ minWidth: 190 }}
          onChange={(event) =>
            changeFilters({
              ...query,
              page: 1,
              problemDays: Number(event.target.value) || undefined,
            })
          }
        >
          <MenuItem value={0}>Любое время</MenuItem>
          {[1, 7, 14, 30, 90].map((days) => (
            <MenuItem key={days} value={days}>
              Не менее {days} дн.
            </MenuItem>
          ))}
        </TextField>
        {!archived && (
          <FormControlLabel
            label="Кандидаты в архив"
            control={
              <Checkbox
                checked={query.candidates === 'yes'}
                onChange={(_, value) =>
                  changeFilters({ ...query, page: 1, candidates: value ? 'yes' : undefined })
                }
              />
            }
          />
        )}
      </Stack>
      {query.candidates === 'yes' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {Object.entries(DONOR_CANDIDATE_RULES)
            .map(
              ([kind, rule]) =>
                `${PROBLEM_LABELS[kind as keyof typeof PROBLEM_LABELS]}: от ${rule.days} дн. и ${rule.count} подтверждений`,
            )
            .join('; ')}
          . Интервал между подтверждениями — от {DONOR_CONFIRMATION_GAP_HOURS} ч., последний
          подтверждающий результат — не старше {DONOR_CANDIDATE_FRESH_HOURS} ч. Архивация
          выполняется вручную.
        </Alert>
      )}
      <Stack direction="row" gap={1} mb={2} flexWrap="wrap" alignItems="center">
        <Typography>Выбрано: {selectedCount}</Typography>
        <EditorOnly any permissions={['donors.run', 'donors.edit', 'donors.delete']}>
          <Button
            disabled={!data?.total || busy}
            onClick={() => {
              setChecked([]);
              setAllMatching(true);
            }}
          >
            Выбрать все найденные ({data?.total ?? 0})
          </Button>
        </EditorOnly>
        <EditorOnly any permissions={['donors.run', 'donors.edit', 'donors.delete']}>
          <Button
            disabled={!selectedCount || busy}
            onClick={() => {
              setChecked([]);
              setAllMatching(false);
            }}
          >
            Снять выбор
          </Button>
        </EditorOnly>
        {(archived ? (['restore', 'delete'] as const) : (['recheck', 'archive'] as const)).map(
          (action) => (
            <EditorOnly permissions={donorBulkPermission(action)!} key={action}>
              <Button
                key={action}
                disabled={busy || !data || !selectedCount}
                color={action === 'delete' ? 'error' : 'primary'}
                onClick={() => openBulk(action)}
              >
                {DONOR_ACTION_LABELS[action]}
              </Button>
            </EditorOnly>
          ),
        )}
      </Stack>
      <Card>
        <TableContainer>
          <Table
            size="small"
            sx={{
              minWidth: 1180,
              tableLayout: 'fixed',
              '& td': { verticalAlign: 'top', overflowWrap: 'anywhere' },
              '& .MuiChip-root': { maxWidth: '100%', height: 'auto', minHeight: 24 },
              '& .MuiChip-label': { whiteSpace: 'normal' },
              '& .MuiButton-root': { whiteSpace: 'nowrap' },
            }}
          >
            <colgroup>
              <col style={{ width: 48 }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '22%' }} />
              <col />
              <col style={{ width: 132 }} />
              <col style={{ width: 145 }} />
              <col style={{ width: 120 }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <EditorOnly any permissions={['donors.run', 'donors.edit', 'donors.delete']}>
                    <Checkbox
                      slotProps={{ input: { 'aria-label': 'Выбрать текущую страницу' } }}
                      checked={
                        Boolean(data?.items.length) &&
                        (allMatching || data!.items.every((row) => checked.includes(row.id)))
                      }
                      indeterminate={
                        !allMatching &&
                        Boolean(data?.items.some((row) => checked.includes(row.id))) &&
                        !data?.items.every((row) => checked.includes(row.id))
                      }
                      onChange={(_, value) => {
                        setAllMatching(false);
                        setChecked(
                          value
                            ? [
                                ...new Set([
                                  ...checked,
                                  ...(data?.items.map((row) => row.id) ?? []),
                                ]),
                              ]
                            : checked.filter((id) => !data?.items.some((row) => row.id === id)),
                        );
                      }}
                    />
                  </EditorOnly>
                </TableCell>
                <TableCell>Статья</TableCell>
                <TableCell>Цель и анкор</TableCell>
                <TableCell>Результат</TableCell>
                <TableCell>Полный HTML</TableCell>
                <TableCell>{archived ? 'Архивация' : 'Следующая проверка'}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.items.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell padding="checkbox">
                    <EditorOnly any permissions={['donors.run', 'donors.edit', 'donors.delete']}>
                      <Checkbox
                        slotProps={{ input: { 'aria-label': `Выбрать ${row.url}` } }}
                        checked={allMatching || checked.includes(row.id)}
                        disabled={allMatching}
                        onChange={(_, value) =>
                          setChecked(
                            value ? [...checked, row.id] : checked.filter((id) => id !== row.id),
                          )
                        }
                      />
                    </EditorOnly>
                  </TableCell>
                  <TableCell>
                    <Link href={row.url} target="_blank" rel="noopener noreferrer">
                      {row.sourceHost}
                    </Link>
                    <Typography fontSize={11} color="text.secondary">
                      {new URL(row.url).pathname}
                    </Typography>
                    {row.url.startsWith('http:') && (
                      <Chip size="small" label="HTTP · без защиты транспорта" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack gap={1.5}>
                      {row.links.slice(0, 2).map((link) => (
                        <Stack key={link.id} gap={0.4}>
                          <Tooltip title={link.target} describeChild>
                            <Typography
                              tabIndex={0}
                              fontSize={12}
                              sx={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2,
                                overflow: 'hidden',
                              }}
                            >
                              {link.target}
                            </Typography>
                          </Tooltip>
                          <Tooltip title={link.anchor || 'Любой анкор'} describeChild>
                            <Typography
                              tabIndex={0}
                              fontSize={12}
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2,
                                overflow: 'hidden',
                              }}
                            >
                              {link.anchor || 'Любой анкор'}
                            </Typography>
                          </Tooltip>
                        </Stack>
                      ))}
                    </Stack>
                    {row.links.length > 2 && (
                      <Typography fontSize={12}>Ещё {row.links.length - 2}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={color(row.state.status)}
                      label={labels[row.state.status]}
                    />
                    {row.problem?.kind && (
                      <Typography fontSize={11}>
                        {PROBLEM_LABELS[row.problem.kind]} · с {date(row.problem.since)} ·
                        подтверждений: {row.problem.confirmations}
                      </Typography>
                    )}
                    {!archived && row.queued && (
                      <Typography fontSize={11}>В очереди / проверяется</Typography>
                    )}
                    {row.state.method === 'conditional' && row.state.httpStatus === 304 && (
                      <Typography fontSize={11}>304 · по метке сервера</Typography>
                    )}
                    {row.deferredUntil && (
                      <Typography fontSize={11}>
                        Отложено до {date(row.deferredUntil)}: {row.deferredReason}
                      </Typography>
                    )}
                    {row.state.error && (
                      <Typography fontSize={11} color="text.secondary" sx={{ maxWidth: 220 }}>
                        {row.state.error}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{date(row.state.lastFullAt)}</TableCell>
                  <TableCell>
                    {archived ? (
                      <>
                        <Typography fontSize={12}>{date(row.archive?.at ?? null)}</Typography>
                        <Typography fontSize={11} sx={{ maxWidth: 220, whiteSpace: 'normal' }}>
                          {row.archive?.reason ?? 'Архивировано ранее'}
                        </Typography>
                      </>
                    ) : (
                      <>
                        {date(row.nextCheckAt)}
                        <Typography fontSize={11} color="text.secondary">
                          Интервал {row.state.intervalDays} дн.
                        </Typography>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => void openDetail(row)}>
                      Подробнее
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!data?.items.length && (
                <TableRow>
                  <TableCell colSpan={7}>
                    {data ? 'Статей по выбранным фильтрам не найдено.' : 'Загрузка…'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data?.total ?? 0}
          page={query.page - 1}
          rowsPerPage={50}
          rowsPerPageOptions={[50]}
          onPageChange={(_, page) => {
            setData(null);
            setQuery({ ...query, page: page + 1 });
          }}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
        />
      </Card>
      {bulkInput && (
        <DonorBulkDialog
          workspace={workspace}
          input={bulkInput}
          onClose={() => setBulkInput(null)}
          onDone={(result) => {
            setBulkInput(null);
            setChecked([]);
            setAllMatching(false);
            setSelected(null);
            setNotice(
              `Обработано: ${result.changed}; пропущено изменившихся: ${result.skipped}; всего: ${result.total}.`,
            );
            void refresh();
          }}
        />
      )}
      {settingsOpen && (
        <DonorSettingsDialog
          key={workspace}
          workspace={workspace}
          onClose={() => setSettingsOpen(false)}
        />
      )}
      {importFile && (
        <DonorImportDialog
          file={importFile}
          workspace={workspace}
          onClose={() => setImportFile(null)}
          onStarted={() => {
            setImportFile(null);
            setImportRevision((value) => value + 1);
            setNotice('Импорт поставлен в очередь. Прогресс показан в блоке «Импорт CSV».');
          }}
        />
      )}
      <Dialog open={confirmAll} onClose={() => setConfirmAll(false)}>
        <DialogTitle>Перепроверить все статьи?</DialogTitle>
        <DialogContent>
          Будет запланирована полная загрузка HTML всех активных статей. Общие ограничения нагрузки
          сохранятся.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAll(false)}>Отмена</Button>
          <EditorOnly permissions="donors.run">
            <Button
              onClick={() =>
                void action(async () => {
                  await api.checkDonors(workspace);
                  setConfirmAll(false);
                }, 'Полная перепроверка запланирована.')
              }
              disabled={busy}
            >
              Запланировать
            </Button>
          </EditorOnly>
        </DialogActions>
      </Dialog>
      {editing && (
        <DonorEditor
          key={editing === 'new' ? 'new' : editing.id}
          workspace={workspace}
          onOpenExisting={(id) => action(async () => setEditing(await api.donor(workspace, id)))}
          article={editing === 'new' ? null : editing}
          busy={busy}
          error={error}
          onClose={() => setEditing(null)}
          onSave={(payload) =>
            action(async () => {
              await api.saveDonor(workspace, payload, editing === 'new' ? undefined : editing.id);
              setEditing(null);
            }, 'Статья сохранена.')
          }
        />
      )}
      <Dialog open={selected !== null} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>Статья и история проверок</DialogTitle>
        <DialogContent>
          {selected && (
            <>
              <Link
                href={selected.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ overflowWrap: 'anywhere' }}
              >
                {selected.url}
              </Link>
              <Typography my={1}>{selected.note}</Typography>
              {selected.archived && (
                <Alert severity="info">
                  В архиве: {selected.archive?.reason ?? 'Архивировано ранее'}. Сохранение правок не
                  возобновляет мониторинг; используйте «Восстановить».
                </Alert>
              )}
              <Evidence state={selected.state} />
              {selected.links.map((link) => (
                <Typography key={link.id} fontSize={13} sx={{ overflowWrap: 'anywhere' }}>
                  {link.target} · ожидаемый анкор: {link.anchor || 'любой'}
                </Typography>
              ))}
              <Divider sx={{ my: 2 }} />
              <Typography variant="h3" mb={1}>
                Последние проверки
              </Typography>
              {actions.map((item) => (
                <Typography key={item.id} variant="body2">
                  {date(item.at)} · {DONOR_ACTION_LABELS[item.action]} · {item.reason} ·
                  пользователь {item.by}
                </Typography>
              ))}
              {history.length ? (
                history.map((item) => (
                  <Card key={item.id} variant="outlined" sx={{ mb: 1 }}>
                    <CardContent>
                      <Typography fontWeight={700}>
                        {date(item.checkedAt)} · {labels[item.state.status]}
                      </Typography>
                      <Evidence state={item.state} />
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Typography>История пока пуста.</Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {selected && (
            <>
              <EditorOnly permissions={selected.archived ? 'donors.edit' : 'donors.delete'}>
                <Button
                  disabled={busy}
                  onClick={() => {
                    setSelected(null);
                    setBulkInput({
                      action: selected.archived ? 'restore' : 'archive',
                      filter: { scope: selected.archived ? 'archived' : 'active' },
                      ids: [selected.id],
                    });
                  }}
                >
                  {selected.archived ? 'Восстановить' : 'Архивировать'}
                </Button>
              </EditorOnly>
              <EditorOnly permissions="donors.edit">
                <Button
                  disabled={busy}
                  onClick={() => {
                    setEditing(selected);
                    setSelected(null);
                  }}
                >
                  Редактировать
                </Button>
              </EditorOnly>
              <EditorOnly permissions="donors.run">
                <Button
                  disabled={busy || selected.archived}
                  onClick={() =>
                    void action(
                      () => api.checkDonors(workspace, selected.id),
                      'Полная проверка запланирована.',
                    )
                  }
                >
                  Перепроверить
                </Button>
              </EditorOnly>
            </>
          )}
          <Button onClick={() => setSelected(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
function Evidence({ state }: { state: DonorState }) {
  return (
    <Stack gap={0.5} my={1}>
      <Typography fontSize={12}>
        Попытка: {date(state.lastAttemptAt)} · Полный HTML: {date(state.lastFullAt)}
      </Typography>
      <Typography fontSize={12}>
        HTTP: {state.httpStatus ?? '—'} ·{' '}
        {state.method === 'conditional' && state.httpStatus === 304
          ? 'Сервер сообщил: версия не изменилась'
          : state.method === 'full'
            ? 'Полная загрузка'
            : 'Нет подтверждения'}
      </Typography>
      {state.finalUrl && (
        <Typography fontSize={12} sx={{ overflowWrap: 'anywhere' }}>
          Адрес последнего разобранного HTML: {state.finalUrl}
        </Typography>
      )}
      {state.validatorWarning && <Alert severity="warning">{state.validatorWarning}</Alert>}
      {state.error && <Alert severity="warning">{state.error}</Alert>}
      {state.evidence.map((row) => (
        <Typography key={row.id} fontSize={12} sx={{ overflowWrap: 'anywhere' }}>
          {row.status === 'missing'
            ? 'Ожидаемая ссылка не найдена'
            : row.matches
                .map(
                  (link) =>
                    `${link.href} · ${link.anchor} · rel: ${link.rel.join(', ') || 'не задан'}`,
                )
                .join('; ')}
        </Typography>
      ))}
      {state.lastFullAt && (
        <Typography fontSize={11} color="text.secondary">
          Ссылки выше — свидетельство последнего полного разбора HTML.
        </Typography>
      )}
    </Stack>
  );
}
function DonorEditor({
  workspace,
  onOpenExisting,
  article,
  busy,
  error,
  onClose,
  onSave,
}: {
  workspace: string;
  onOpenExisting: (id: string) => Promise<void>;
  article: DonorArticleView | null;
  busy: boolean;
  error: string;
  onClose: () => void;
  onSave: (payload: {
    url: string;
    note: string;
    links: Array<{ target: string; anchor: string }>;
    revision?: number;
  }) => Promise<void>;
}) {
  const [url, setUrl] = useState(article?.sourceInput ?? '');
  const [note, setNote] = useState(article?.note ?? '');
  const [links, setLinks] = useState(
    article?.links.map(({ target, anchor }) => ({ target, anchor })) ?? [
      { target: '', anchor: '' },
    ],
  );
  const [matches, setMatches] = useState<DonorLookupItem[]>([]);
  const [lookupError, setLookupError] = useState('');
  useEffect(() => {
    let current = true;
    const timer = setTimeout(() => {
      if (!url.trim()) return;
      void api.lookupDonors(workspace, url).then(
        (items) => {
          if (current) {
            setMatches(items);
            setLookupError('');
          }
        },
        (caught: unknown) => {
          if (current) setLookupError(message(caught));
        },
      );
    }, 300);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [workspace, url]);
  const duplicate = matches.some((item) => item.exact && item.id !== article?.id);
  function submit(event: FormEvent) {
    event.preventDefault();
    if (duplicate) return;
    void onSave({ url, note, links, revision: article?.revision });
  }
  return (
    <Dialog open onClose={() => !busy && onClose()} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <DialogTitle>{article ? 'Редактировать статью' : 'Добавить статью'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack gap={2} mt={1}>
            <TextField
              required
              label="URL статьи"
              value={url}
              onChange={(event) => {
                setUrl(event.target.value);
                setMatches([]);
                setLookupError('');
              }}
              slotProps={{ htmlInput: { maxLength: 2048 } }}
            />
            {lookupError && (
              <Alert severity="warning">Поиск совпадений недоступен: {lookupError}</Alert>
            )}
            {duplicate && (
              <Alert severity="warning">Такая статья уже есть. Откройте существующую запись.</Alert>
            )}
            {matches
              .filter((item) => item.id !== article?.id)
              .map((item) => (
                <Stack key={item.id} gap={0.5}>
                  <Typography fontSize={12} sx={{ overflowWrap: 'anywhere' }}>
                    {item.url}
                    {item.archived ? ' · в архиве' : ''}
                  </Typography>
                  <Button size="small" disabled={busy} onClick={() => void onOpenExisting(item.id)}>
                    Открыть существующую статью{item.archived ? ' в архиве' : ''}
                  </Button>
                </Stack>
              ))}
            {links.map((link, index) => (
              <Stack key={index} gap={1}>
                <TextField
                  required
                  label="Целевой домен или URL"
                  value={link.target}
                  onChange={(event) =>
                    setLinks(
                      links.map((item, i) =>
                        i === index ? { ...item, target: event.target.value } : item,
                      ),
                    )
                  }
                />
                <TextField
                  label="Ожидаемый анкор"
                  value={link.anchor}
                  onChange={(event) =>
                    setLinks(
                      links.map((item, i) =>
                        i === index ? { ...item, anchor: event.target.value } : item,
                      ),
                    )
                  }
                />
                {links.length > 1 && (
                  <Button onClick={() => setLinks(links.filter((_, i) => i !== index))}>
                    Убрать ссылку
                  </Button>
                )}
              </Stack>
            ))}
            <Button
              disabled={links.length >= 50}
              onClick={() => setLinks([...links, { target: '', anchor: '' }])}
            >
              Ещё ссылка
            </Button>
            <TextField
              label="Заметка"
              multiline
              minRows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={onClose}>
            Отмена
          </Button>
          <Button disabled={busy || duplicate} type="submit" variant="contained">
            Сохранить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
